import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Table,
  Button,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Input,
  InputGroup,
  InputGroupAddon,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import Widget from '../../../components/Widget';
import config from '../../../config';
import { updateUnlockedCount } from '../../../actions/unlockedBookings';
import { useDateFilter } from '../../../contexts/DateFilterContext';
import { useCity } from '../../../contexts/CityContext';
import AssignUnlocked from './AssignUnlocked';

// Configure axios for unlocked bookings API (dispatch/assignment API)
const unlockedBookingsApi = axios.create({
  baseURL: config.dispatchApiUrl, // api_server.py runs on port 8001
});

const opsApi = axios.create({
  baseURL: config.opsApiBase, // main.py FastAPI runs on port 8000 (/api/v1/ops)
});

// Pagination styles
const paginationStyles = {
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    gap: '20px',
    borderTop: '1px solid #e5e7eb'
  },
  pageBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    background: 'white',
    borderRadius: '8px',
    color: '#374151',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  pageBtnDisabled: {
    opacity: '0.5',
    cursor: 'not-allowed',
    background: '#f3f4f6'
  },
  pageInfo: {
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: '500'
  }
};

class UnlockedBookings extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTab: 'regular',
      searchTerm: '',
      bookings: [],
      hourlyBookings: [],
      isFetching: false,
      isFetchingHourly: false,
      error: null,
      lastUpdated: null,
      // Pagination state
      currentPage: 1,
      itemsPerPage: 20,
      // Auto-refresh countdown
      refreshIntervalSeconds: 10,
      nextRefreshIn: 10,
      // Edit modal state
      editModalOpen: false,
      editBooking: null,
      editPickupDate: '',
      editPickupTime: '',
      editPickupAddress: '',
      editDropAddress: '',
      editVehicleType: '',
      // Autocomplete suggestions and lat/lng for addresses
      editPickupSuggestions: [],
      editDropSuggestions: [],
      editPickupLat: null,
      editPickupLng: null,
      editDropLat: null,
      editDropLng: null,
      editPickupPlaceId: null,
      editDropPlaceId: null,
      // 🆕 NEW: Assign vehicle modal state (simplified)
      assignVehicleModalOpen: false,
      assignVehicleBooking: null,
      assignVehicleMode: 'future',
      // Ops Agent Trigger State
      opsAgentTriggers: [],
      opsTriggerModalOpen: false,
      selectedTrigger: null,
    };

    // Countdown timer for auto-refresh
    this.countdownTimer = null;
  }


  componentDidMount() {
    this.fetchUnlockedBookings();
    this.fetchHourlyUnassignedBookings();
    this.fetchOpsAgentTriggers();
    // Set up auto-refresh every 10 seconds
    this.refreshInterval = setInterval(() => {
      this.fetchUnlockedBookings();
      this.fetchHourlyUnassignedBookings();
      this.fetchOpsAgentTriggers();
    }, 10000);
    // Start countdown timer
    this.countdownTimer = setInterval(() => {
      this.setState(prev => ({
        nextRefreshIn: prev.nextRefreshIn > 0 ? prev.nextRefreshIn - 1 : 0
      }));
    }, 1000);
  }

  componentDidUpdate(prevProps) {
    // Refetch when selected cities change
    if (JSON.stringify(prevProps.selectedCities) !== JSON.stringify(this.props.selectedCities)) {
      this.fetchUnlockedBookings();
      this.fetchHourlyUnassignedBookings();
      this.fetchOpsAgentTriggers();
    }
  }

  setActiveTab = (tab) => {
    this.setState({ activeTab: tab, currentPage: 1 });
  }

  componentWillUnmount() {
    // Clear interval when component unmounts
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  fetchOpsAgentTriggers = async () => {
    try {
      const response = await unlockedBookingsApi.get('/ops-agent-triggers');
      if (response.data && response.data.success) {
        this.setState({ opsAgentTriggers: response.data.data || [] });
      }
    } catch (error) {
      console.warn('Silent skip - Ops Agent triggers endpoint error:', error);
    }
  }

  openOpsTriggerModal = (trigger) => {
    this.setState({
      selectedTrigger: trigger,
      opsTriggerModalOpen: true
    });
  }

  closeOpsTriggerModal = () => {
    this.setState({
      selectedTrigger: null,
      opsTriggerModalOpen: false
    });
  }

  fetchUnlockedBookings = async () => {
    this.setState({ isFetching: true, error: null });
    try {
      // Build query parameters
      const params = new URLSearchParams();

      // Add cities filter if any cities are selected
      if (this.props.selectedCities && this.props.selectedCities.length > 0) {
        params.append('cities', this.props.selectedCities.join(','));
      }

      const queryString = params.toString();
      const url = `/unlocked-bookings${queryString ? `?${queryString}` : ''}`;

      const response = await unlockedBookingsApi.get(url);

      if (response.data.success) {
        const list = response.data.data || [];
        this.props.dispatch(updateUnlockedCount(list.length));
        this.setState({
          bookings: list,
          isFetching: false,
          lastUpdated: response.data.timestamp,
          nextRefreshIn: this.state.refreshIntervalSeconds,
        });
      } else {
        throw new Error('Failed to fetch unlocked bookings');
      }
    } catch (error) {
      console.error('Failed to fetch unlocked bookings:', error);
      this.setState({
        error: error.response?.data?.detail || error.message || 'Failed to fetch unlocked bookings',
        isFetching: false
      });
      toast.error('Failed to fetch unlocked bookings');
    }
  }

  fetchHourlyUnassignedBookings = async () => {
    this.setState({ isFetchingHourly: true });
    try {
      const params = new URLSearchParams();
      if (this.props.selectedCities && this.props.selectedCities.length > 0) {
        params.append('cities', this.props.selectedCities.join(','));
      }

      const queryString = params.toString();
      const url = `/hourly-unassigned-bookings${queryString ? `?${queryString}` : ''}`;
      const response = await opsApi.get(url);

      if (response.data?.success) {
        this.setState({
          hourlyBookings: response.data.data || [],
          isFetchingHourly: false,
          nextRefreshIn: this.state.refreshIntervalSeconds,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to fetch hourly unassigned bookings');
      }
    } catch (error) {
      console.warn('Hourly unassigned bookings endpoint not available:', error.message);
      // Don't set error state - this endpoint is optional
      this.setState({
        hourlyBookings: [],
        isFetchingHourly: false,
      });
    }
  }

  handleSearch = (e) => {
    this.setState({ searchTerm: e.target.value });
  }

  filterBookings = (bookings) => {
    const { searchTerm } = this.state;

    let filtered = bookings;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(booking => {
        const matchesSearch =
          String(booking.id || booking.booking_id).includes(searchTerm) ||
          (booking.pickup_location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (booking.drop_location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (booking.vehicle_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          String(booking.assigned_vehicle_id || '').includes(searchTerm);
        return matchesSearch;
      });
    }

    // Apply date filter
    if (this.props.isDateInRange) {
      filtered = filtered.filter(booking => {
        const bookingDate = booking.pickup_time || booking.created_at;
        return !bookingDate || this.props.isDateInRange(bookingDate);
      });
    }

    return filtered;
  }

  filterHourlyBookings = (bookings) => {
    const { searchTerm } = this.state;
    let filtered = bookings;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => {
        const matchesSearch =
          String(booking.booking_id || '').includes(q) ||
          String(booking.vehicle_class_id || '').includes(q) ||
          (booking.pickup_location || '').toLowerCase().includes(q) ||
          (booking.drop_location || '').toLowerCase().includes(q) ||
          (booking.reason || '').toLowerCase().includes(q);
        return matchesSearch;
      });
    }

    if (this.props.isDateInRange) {
      filtered = filtered.filter(booking => {
        const bookingDate = booking.pickup_time || booking.last_failed_at;
        return !bookingDate || this.props.isDateInRange(bookingDate);
      });
    }

    return filtered;
  }

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  }

  getVehicleTypeBadge = (vehicleType) => {
    const typeMap = {
      class1: 'danger',
      class2: 'primary',
      class3: 'success',
      class4: 'warning',
      class5: 'info',
      class6: 'secondary',
      class7: 'dark',
    };
    return typeMap[vehicleType] || 'secondary';
  }

  formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  }

  formatLastUpdated = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-IN');
    } catch (e) {
      return '';
    }
  }

  handleEditBooking = (booking) => {
    // Open the edit modal and prefill values
    this.openEditModal(booking);
  }

  openEditModal = (booking) => {
    const pickup = booking.pickup_time ? new Date(booking.pickup_time) : new Date();
    // date input expects YYYY-MM-DD, time expects HH:MM
    const dateStr = pickup.toISOString().slice(0, 10);
    // Use local time portion for time input
    const hh = String(pickup.getHours()).padStart(2, '0');
    const mm = String(pickup.getMinutes()).padStart(2, '0');
    const timeStr = `${hh}:${mm}`;

    this.setState({
      editModalOpen: true,
      editBooking: booking,
      editPickupDate: dateStr,
      editPickupTime: timeStr,
      editPickupAddress: booking.pickup_location || '',
      editDropAddress: booking.drop_location || '',
      editVehicleType: booking.vehicle_type || '',
    });
  }

  closeEditModal = () => {
    this.setState({
      editModalOpen: false,
      editBooking: null,
      editPickupDate: '',
      editPickupTime: '',
      editPickupAddress: '',
      editDropAddress: '',
      editVehicleType: '',
    });
  }

  handleEditInputChange = (e) => {
    const { name, value } = e.target;
    // Update value
    this.setState({ [name]: value }, () => {
      // If user is typing pickup or drop address, trigger autocomplete
      if (name === 'editPickupAddress') {
        this.triggerAddressAutocomplete(value, true);
      } else if (name === 'editDropAddress') {
        this.triggerAddressAutocomplete(value, false);
      }
    });
  }

  handleAddressBlur = (isPickup) => {
    // Delay clearing suggestions to allow click handler to fire
    setTimeout(() => {
      if (isPickup) this.setState({ editPickupSuggestions: [] });
      else this.setState({ editDropSuggestions: [] });
    }, 200);
  }

  handleAddressFocus = (isPickup) => {
    const val = isPickup ? this.state.editPickupAddress : this.state.editDropAddress;
    if (val) this.fetchPlacePredictions(val, isPickup);
  }

  triggerAddressAutocomplete = (text, isPickup) => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return; // API key not configured

    // clear previous timer
    if (isPickup) {
      if (this.pickupTimer) clearTimeout(this.pickupTimer);
      this.pickupTimer = setTimeout(() => this.fetchPlacePredictions(text, true), 300);
    } else {
      if (this.dropTimer) clearTimeout(this.dropTimer);
      this.dropTimer = setTimeout(() => this.fetchPlacePredictions(text, false), 300);
    }
  }

  fetchPlacePredictions = async (inputText, isPickup) => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key || !inputText) {
      if (isPickup) this.setState({ editPickupSuggestions: [] });
      else this.setState({ editDropSuggestions: [] });
      return;
    }

    try {
      const url = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(inputText)}&key=${key}&types=geocode&language=en`;
      const res = await fetch(url);
      const data = await res.json();
      const suggestions = (data.predictions || []).map(p => ({ description: p.description, place_id: p.place_id }));
      if (isPickup) this.setState({ editPickupSuggestions: suggestions });
      else this.setState({ editDropSuggestions: suggestions });
    } catch (e) {
      console.error('Autocomplete error', e);
    }
  }

  selectSuggestion = async (suggestion, isPickup) => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) {
      toast.error('Google Maps API key not configured');
      return;
    }

    try {
      // Get place details to obtain lat/lng
      const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${suggestion.place_id}&key=${key}`;
      const res = await fetch(url);
      const data = await res.json();
      const loc = data.result?.geometry?.location;
      const formatted = data.result?.formatted_address || suggestion.description;

      if (isPickup) {
        this.setState({
          editPickupAddress: formatted,
          editPickupLat: loc?.lat ?? null,
          editPickupLng: loc?.lng ?? null,
          editPickupPlaceId: suggestion.place_id,
          editPickupSuggestions: [],
        });
      } else {
        this.setState({
          editDropAddress: formatted,
          editDropLat: loc?.lat ?? null,
          editDropLng: loc?.lng ?? null,
          editDropPlaceId: suggestion.place_id,
          editDropSuggestions: [],
        });
      }
    } catch (e) {
      console.error('Place details error', e);
      toast.error('Failed to fetch place details');
    }
  }
  handleAssignVehicle = (booking, mode = 'future') => {
    this.setState({
      assignVehicleModalOpen: true,
      assignVehicleBooking: booking,
      assignVehicleMode: mode,
    });
  }

  closeAssignVehicleModal = () => {
    this.setState({
      assignVehicleModalOpen: false,
      assignVehicleBooking: null,
      assignVehicleMode: 'future',
    });
  }

  handleAssignmentSuccess = () => {
    // Refresh the bookings list after successful assignment
    this.fetchUnlockedBookings();
    this.fetchHourlyUnassignedBookings();
  }

  handleSaveEdit = () => {
    const {
      editBooking,
      editPickupDate,
      editPickupTime,
      editPickupAddress,
      editDropAddress,
      editVehicleType,
      editPickupLat,
      editPickupLng,
      editDropLat,
      editDropLng,
      editPickupPlaceId,
      editDropPlaceId,
      bookings,
    } = this.state;

    if (!editBooking) return;

    // Basic validation
    if (!editPickupDate || !editPickupTime) {
      toast.error('Please enter both pickup date and time');
      return;
    }

    // Build payload for backend
    const payload = {
      pickup_date: editPickupDate,
      pickup_time: editPickupTime,
    };

    if (editPickupAddress) {
      payload.pickup_address = editPickupAddress;
      if (editPickupLat != null) {
        payload.pickup_lat = editPickupLat;
        payload.pickup_lng = editPickupLng;
      }
      if (editPickupPlaceId) payload.pickup_place_id = editPickupPlaceId;
    }

    if (editDropAddress) {
      payload.drop_address = editDropAddress;
      if (editDropLat != null) {
        payload.drop_lat = editDropLat;
        payload.drop_lng = editDropLng;
      }
      if (editDropPlaceId) payload.drop_place_id = editDropPlaceId;
    }

    // Map vehicle type like 'class2' to integer id when possible
    if (editVehicleType) {
      const m = String(editVehicleType).match(/class(\d+)/i);
      if (m) payload.vehicle_class_id = parseInt(m[1], 10);
    }

    const bookingId = editBooking.id || editBooking.booking_id;
    // Call backend
    unlockedBookingsApi.post(`/api/bookings/${bookingId}/edit`, payload)
      .then(resp => {
        if (resp.data && resp.data.success) {
          const updatedBooking = resp.data.booking;
          // Update local bookings array: map and replace matching booking
          const updatedList = bookings.map(b => {
            const id = b.id || b.booking_id;
            if (id === bookingId) {
              // Merge returned columns into our booking row representation
              return {
                ...b,
                ...updatedBooking,
              };
            }
            return b;
          });
          this.setState({ bookings: updatedList }, () => {
            toast.success(`Booking #${bookingId} updated`);
            this.closeEditModal();
          });
        } else {
          throw new Error('Invalid response from server');
        }
      })
      .catch(err => {
        console.error('Failed to save booking edit', err);
        toast.error('Failed to save booking');
      });
  }





  render() {
    const { activeTab, searchTerm, bookings, hourlyBookings, isFetching, isFetchingHourly, error, lastUpdated, currentPage, itemsPerPage } = this.state;
    const filteredBookings = this.filterBookings(bookings);
    const filteredHourlyBookings = this.filterHourlyBookings(hourlyBookings);
    const activeData = activeTab === 'hourly' ? filteredHourlyBookings : filteredBookings;

    if (error) {
      return (
        <div>
          <Breadcrumb>
            <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
            <BreadcrumbItem>
              <Link to="/app/trips">Trips</Link>
            </BreadcrumbItem>
            <BreadcrumbItem active>Unlocked Bookings</BreadcrumbItem>
          </Breadcrumb>

          <Row>
            <Col sm={12}>
              <Widget title="Unlocked Bookings">
                <div className="alert alert-danger">
                  Error loading unlocked bookings: {error}
                  <br />
                  <Button color="primary" onClick={this.fetchUnlockedBookings} className="mt-2">
                    Retry
                  </Button>
                </div>
              </Widget>
            </Col>
          </Row>
        </div>
      );
    }

    return (
      <div>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem>
            <Link to="/app/trips">Trips</Link>
          </BreadcrumbItem>
          <BreadcrumbItem active>Unlocked Bookings</BreadcrumbItem>
        </Breadcrumb>

        <Row>
          <Col sm={12}>
            <Widget
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mt-0 mb-0">
                    <i className="fa fa-unlock-alt mr-xs opacity-70" />{' '}
                    Unlocked Bookings
                  </h5>
                  <div className="d-flex align-items-center">
                    {lastUpdated && (
                      <small className="text-muted mr-3">
                        <i className="fa fa-clock-o mr-1" />
                        Last updated: {this.formatLastUpdated(lastUpdated)}
                      </small>
                    )}
                    <Link
                      to="/app/trips"
                      className="btn btn-secondary btn-sm"
                    >
                      <i className="fa fa-arrow-left mr-2" />
                      Back to Trips
                    </Link>
                  </div>
                </div>
              }
            >
              <Row className="mb-3">
                <Col sm={12} className="mb-3">
                  <div className="btn-group" role="group" aria-label="unassigned-tabs">
                    <Button
                      color={activeTab === 'regular' ? 'primary' : 'secondary'}
                      onClick={() => this.setActiveTab('regular')}
                    >
                      Regular Unassigned ({filteredBookings.length})
                    </Button>
                    <Button
                      color={activeTab === 'hourly' ? 'primary' : 'secondary'}
                      onClick={() => this.setActiveTab('hourly')}
                    >
                      Hourly Unassigned ({filteredHourlyBookings.length})
                    </Button>
                  </div>
                </Col>
                <Col sm={6}>
                  <InputGroup>
                    <Input
                      placeholder="Search bookings by ID, location, or vehicle type..."
                      value={searchTerm}
                      onChange={this.handleSearch}
                    />
                    <InputGroupAddon addonType="append">
                      <Button color="default">
                        <i className="fa fa-search" />
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                </Col>
                <Col sm={6} className="text-right">
                  <Button
                    color="primary"
                    onClick={() => {
                      this.fetchUnlockedBookings();
                      this.fetchHourlyUnassignedBookings();
                    }}
                    disabled={isFetching || isFetchingHourly}
                    title={`Auto-refreshing in ${this.state.nextRefreshIn}s`}
                  >
                    <i className={`fa fa-refresh ${(isFetching || isFetchingHourly) ? 'fa-spin' : ''} mr-2`} />
                    Refresh {!(isFetching || isFetchingHourly) && `(${this.state.nextRefreshIn}s)`}
                  </Button>
                </Col>
              </Row>

              {activeData.length > 0 && (
                <div className="alert alert-info mb-3">
                  <i className="fa fa-info-circle mr-2" />
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, activeData.length)}-{Math.min(currentPage * itemsPerPage, activeData.length)} of {activeData.length} {activeTab === 'hourly' ? 'hourly unassigned' : 'unlocked'} bookings
                </div>
              )}

              {activeTab === 'regular' ? (
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Vehicle Type</th>
                    <th>Suggested Vehicle</th>
                    <th>Pickup Location</th>
                    <th>Drop Location</th>
                    <th>Pickup Time</th>
                    <th>Distance</th>
                    <th>Duration</th>
                    <th>Fare</th>
                    <th>Agent</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isFetching && bookings.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center">
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Loading unlocked bookings...
                      </td>
                    </tr>
                  ) : filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center">
                        {bookings.length === 0 ? (
                          <div className="py-4">
                            <i className="fa fa-check-circle text-success" style={{ fontSize: '48px' }} />
                            <h5 className="mt-3">No Unlocked Bookings</h5>
                            <p className="text-muted">All bookings have been locked or assigned.</p>
                          </div>
                        ) : (
                          <div className="py-4">
                            <i className="fa fa-search" style={{ fontSize: '48px', opacity: 0.5 }} />
                            <h5 className="mt-3">No matching bookings found</h5>
                            <p className="text-muted">Try adjusting your search criteria</p>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredBookings
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map(booking => (
                        <tr key={booking.id || booking.booking_id}>
                          <td>
                            <strong>#{booking.id || booking.booking_id}</strong>
                          </td>
                          <td>
                            <Badge color={this.getVehicleTypeBadge(booking.vehicle_type)}>
                              {booking.vehicle_type || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            {booking.assigned_vehicle_id ? (
                              <span>
                                <strong>#{booking.assigned_vehicle_id}</strong>
                                {' '}
                                <Badge color={this.getVehicleTypeBadge(booking.assigned_vehicle_type)} className="ml-1">
                                  {booking.assigned_vehicle_type || ''}
                                </Badge>
                              </span>
                            ) : (
                              <span className="text-muted">
                                <i className="fa fa-minus-circle mr-1" />
                                Not Assigned
                              </span>
                            )}
                          </td>
                          <td>
                            <small>
                              <i className="fa fa-map-marker text-success mr-1" />
                              {booking.pickup_location ?
                                (booking.pickup_location.length > 40 ?
                                  booking.pickup_location.substring(0, 40) + '...' :
                                  booking.pickup_location) :
                                'N/A'}
                            </small>
                          </td>
                          <td>
                            <small>
                              <i className="fa fa-flag text-danger mr-1" />
                              {booking.drop_location ?
                                (booking.drop_location.length > 40 ?
                                  booking.drop_location.substring(0, 40) + '...' :
                                  booking.drop_location) :
                                'N/A'}
                            </small>
                          </td>
                          <td>
                            <small>
                              <i className="fa fa-clock-o mr-1" />
                              {this.formatDate(booking.pickup_time)}
                            </small>
                          </td>
                          <td>
                            <span className="badge badge-light">
                              {booking.distance ? `${booking.distance} km` : 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span className="badge badge-light">
                              {booking.duration ? `${Math.round(booking.duration)} min` : 'N/A'}
                            </span>
                          </td>
                          <td>
                            <strong className="text-success">
                              ₹{booking.payment_estimate ? booking.payment_estimate.toFixed(2) : 'N/A'}
                            </strong>
                          </td>
                          {/* Agent Column - Ops Agent Button */}
                          <td style={{ textAlign: 'center' }}>
                            {this.state.opsAgentTriggers.find((t) => (t.booking.booking_id || t.booking.id) === (booking.id || booking.booking_id)) ? (
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => this.openOpsTriggerModal(this.state.opsAgentTriggers.find((t) => (t.booking.booking_id || t.booking.id) === (booking.id || booking.booking_id)))}
                                title="Ops Agent Available"
                                style={{
                                  background: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
                                  border: 'none',
                                  boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                                }}
                              >
                                <i className="fa fa-robot mr-1" />
                                🤖 Ops Agent
                              </Button>
                            ) : (
                              <span style={{ color: '#ccc' }}>—</span>
                            )}
                          </td>
                          {/* Actions Column - Edit and Assign buttons */}
                          <td>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => this.handleEditBooking(booking)}
                              title="Edit Booking"
                              className="mr-2"
                            >
                              <i className="fa fa-edit mr-1" />
                              Edit
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => this.handleAssignVehicle(booking, 'future')}
                              title="Assign Vehicle"
                            >
                              <i className="fa fa-car mr-1" />
                              Assign Vehicle
                            </Button>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </Table>
              ) : (
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Vehicle Class</th>
                    <th>Pickup Location</th>
                    <th>Pickup Time</th>
                    <th>Duration</th>
                    <th>Failed Attempts</th>
                    <th>Minutes to Pickup</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isFetchingHourly && hourlyBookings.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Loading hourly unassigned bookings...
                      </td>
                    </tr>
                  ) : filteredHourlyBookings.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        <div className="py-4">
                          <i className="fa fa-check-circle text-success" style={{ fontSize: '48px' }} />
                          <h5 className="mt-3">No Hourly Unassigned Bookings</h5>
                          <p className="text-muted">All near-term hourly bookings are assigned.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredHourlyBookings
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map(booking => (
                        <tr key={booking.booking_id}>
                          <td><strong>#{booking.booking_id}</strong></td>
                          <td>
                            <Badge color="warning">Class {booking.vehicle_class_id || 'N/A'}</Badge>
                          </td>
                          <td>
                            <small>
                              <i className="fa fa-map-marker text-success mr-1" />
                              {booking.pickup_location || 'N/A'}
                            </small>
                          </td>
                          <td>
                            <small><i className="fa fa-clock-o mr-1" />{this.formatDate(booking.pickup_time)}</small>
                          </td>
                          <td>
                            <span className="badge badge-light">
                              {booking.duration ? `${booking.duration} min` : 'N/A'}
                            </span>
                          </td>
                          <td><Badge color="danger">{booking.failed_attempts || 1}</Badge></td>
                          <td>
                            <Badge color={(booking.minutes_to_pickup ?? 999) <= 30 ? 'danger' : 'info'}>
                              {booking.minutes_to_pickup != null ? `${booking.minutes_to_pickup} min` : 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => this.handleAssignVehicle(booking, 'hourly')}
                              title="Assign Vehicle Now"
                            >
                              <i className="fa fa-car mr-1" />Assign Vehicle
                            </Button>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </Table>
              )}

              {/* Pagination Controls */}
              {activeData.length > itemsPerPage && (
                <div style={paginationStyles.pagination}>
                  <button
                    disabled={currentPage === 1}
                    onClick={() => this.handlePageChange(currentPage - 1)}
                    style={{
                      ...paginationStyles.pageBtn,
                      ...(currentPage === 1 ? paginationStyles.pageBtnDisabled : {})
                    }}
                  >
                    <i className="fa fa-chevron-left" /> Previous
                  </button>
                  <span style={paginationStyles.pageInfo}>
                    Page {currentPage} of {Math.ceil(activeData.length / itemsPerPage)}
                  </span>
                  <button
                    disabled={currentPage === Math.ceil(activeData.length / itemsPerPage)}
                    onClick={() => this.handlePageChange(currentPage + 1)}
                    style={{
                      ...paginationStyles.pageBtn,
                      ...(currentPage === Math.ceil(activeData.length / itemsPerPage) ? paginationStyles.pageBtnDisabled : {})
                    }}
                  >
                    Next <i className="fa fa-chevron-right" />
                  </button>
                </div>
              )}

              {/* Edit Booking Modal */}
              <Modal isOpen={this.state.editModalOpen} toggle={this.closeEditModal}>
                <ModalHeader toggle={this.closeEditModal}>Edit Booking #{this.state.editBooking?.id || this.state.editBooking?.booking_id}</ModalHeader>
                <ModalBody>
                  <FormGroup>
                    <Label for="editPickupDate">Pickup Date</Label>
                    <Input
                      type="date"
                      id="editPickupDate"
                      name="editPickupDate"
                      value={this.state.editPickupDate}
                      onChange={this.handleEditInputChange}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label for="editPickupTime">Pickup Time</Label>
                    <Input
                      type="time"
                      id="editPickupTime"
                      name="editPickupTime"
                      value={this.state.editPickupTime}
                      onChange={this.handleEditInputChange}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label for="editPickupAddress">Pickup Address</Label>
                    <Input
                      type="text"
                      id="editPickupAddress"
                      name="editPickupAddress"
                      value={this.state.editPickupAddress}
                      onChange={this.handleEditInputChange}
                      onBlur={() => this.handleAddressBlur(true)}
                      onFocus={() => this.handleAddressFocus(true)}
                    />
                    {this.state.editPickupSuggestions.length > 0 && (
                      <div className="list-group mt-1">
                        {this.state.editPickupSuggestions.map(s => (
                          <button
                            type="button"
                            key={s.place_id}
                            className="list-group-item list-group-item-action"
                            onClick={() => this.selectSuggestion(s, true)}
                          >
                            {s.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label for="editDropAddress">Drop Address</Label>
                    <Input
                      type="text"
                      id="editDropAddress"
                      name="editDropAddress"
                      value={this.state.editDropAddress}
                      onChange={this.handleEditInputChange}
                      onBlur={() => this.handleAddressBlur(false)}
                      onFocus={() => this.handleAddressFocus(false)}
                    />
                    {this.state.editDropSuggestions.length > 0 && (
                      <div className="list-group mt-1">
                        {this.state.editDropSuggestions.map(s => (
                          <button
                            type="button"
                            key={s.place_id}
                            className="list-group-item list-group-item-action"
                            onClick={() => this.selectSuggestion(s, false)}
                          >
                            {s.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label for="editVehicleType">Vehicle Type</Label>
                    <Input
                      type="text"
                      id="editVehicleType"
                      name="editVehicleType"
                      value={this.state.editVehicleType}
                      onChange={this.handleEditInputChange}
                    />
                  </FormGroup>
                </ModalBody>
                <ModalFooter>
                  <Button color="secondary" onClick={this.closeEditModal}>Cancel</Button>
                  <Button color="primary" onClick={this.handleSaveEdit}>Save</Button>
                </ModalFooter>
              </Modal>

              <AssignUnlocked
                isOpen={this.state.assignVehicleModalOpen}
                booking={this.state.assignVehicleBooking}
                assignMode={this.state.assignVehicleMode}
                onClose={this.closeAssignVehicleModal}
                onSuccess={this.handleAssignmentSuccess}
              />

              {/* Ops Agent Trigger Modal */}
              {this.state.selectedTrigger && (
                <Modal isOpen={this.state.opsTriggerModalOpen} toggle={this.closeOpsTriggerModal} size="lg">
                  <ModalHeader toggle={this.closeOpsTriggerModal} className="bg-danger text-white">
                    <i className="fa fa-exclamation-triangle mr-2"></i>
                    Ops Agent Action Required
                  </ModalHeader>
                  <ModalBody>
                    <div className="alert alert-danger mb-4" style={{ whiteSpace: 'pre-wrap' }}>
                      <strong><i className="fa fa-bullhorn mr-2"></i>Action Template:</strong><br/>
                      "{this.state.selectedTrigger.booking.agent_message || "Driver is idle and is close to this pickup. Please call the customer and ask if they could change the pickup time to match the driver's availability."}"
                    </div>
                    
                    <Row>
                      <Col md={6}>
                        <h6 className="border-bottom pb-2 mb-3">Driver Details</h6>
                        <dl className="row mb-0">
                          <dt className="col-sm-5 text-muted">Vehicle ID</dt>
                          <dd className="col-sm-7"><strong>#{this.state.selectedTrigger.driver.vehicle_id}</strong> ({this.state.selectedTrigger.driver.vehicle_type})</dd>
                          
                          <dt className="col-sm-5 text-muted">Contact</dt>
                          <dd className="col-sm-7">{this.state.selectedTrigger.driver.phone_number}</dd>
                          
                          <dt className="col-sm-5 text-muted">Idle Since</dt>
                          <dd className="col-sm-7">{this.formatDate(this.state.selectedTrigger.driver.idle_since)}</dd>
                          
                          <dt className="col-sm-5 text-muted">Distance</dt>
                          <dd className="col-sm-7"><Badge color="info">{this.state.selectedTrigger.booking.distance_to_idle_driver} km away</Badge></dd>
                        </dl>
                      </Col>
                      <Col md={6}>
                        <h6 className="border-bottom pb-2 mb-3">Booking Details</h6>
                        <dl className="row mb-0">
                          <dt className="col-sm-5 text-muted">Booking ID</dt>
                          <dd className="col-sm-7"><strong>#{this.state.selectedTrigger.booking.booking_id || this.state.selectedTrigger.booking.id}</strong></dd>
                          
                          <dt className="col-sm-5 text-muted">Customer Phone</dt>
                          <dd className="col-sm-7">
                            {this.state.selectedTrigger.booking.customer_phone ? (
                              <div className="d-flex align-items-center">
                                <span className="mr-2">{this.state.selectedTrigger.booking.customer_phone}</span>
                                <a href={`tel:${this.state.selectedTrigger.booking.customer_phone}`} className="btn btn-sm btn-outline-success py-0 px-2 rounded-pill">
                                  <i className="fa fa-phone mr-1"></i>Call
                                </a>
                              </div>
                            ) : 'N/A'}
                          </dd>
                          
                          <dt className="col-sm-5 text-muted">Pickup Time</dt>
                          <dd className="col-sm-7 text-danger"><strong>{this.formatDate(this.state.selectedTrigger.booking.pickup_time)}</strong></dd>
                          
                          <dt className="col-sm-5 text-muted">Route</dt>
                          <dd className="col-sm-7">
                            <small className="d-block mb-1"><i className="fa fa-map-marker text-success mr-1"/> {this.state.selectedTrigger.booking.pickup_location}</small>
                            <small className="d-block"><i className="fa fa-flag text-danger mr-1"/> {this.state.selectedTrigger.booking.drop_location}</small>
                          </dd>
                        </dl>
                      </Col>
                    </Row>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="secondary" onClick={this.closeOpsTriggerModal}>Close</Button>
                  </ModalFooter>
                </Modal>
              )}
            </Widget>
          </Col>
        </Row>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {};
}

const ConnectedUnlockedBookings = connect(mapStateToProps)(UnlockedBookings);

// HOC to inject date filter and city context
const UnlockedBookingsWithContexts = (props) => {
  const { isDateInRange } = useDateFilter();
  const { selectedCities } = useCity();
  return <ConnectedUnlockedBookings {...props} isDateInRange={isDateInRange} selectedCities={selectedCities} />;
};

export default UnlockedBookingsWithContexts;
